import React from "react";
import ReactDOM from "react-dom/client";
import App from "./App";
import AppProvider from "./context/AppProvider";
import "./i18n";

import AntdLocaleProvider from "./components/AntdLocaleProvider/AntdLocaleProvider";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { loadAppStringsForLanguage } from "./utils/i18n/loadAppStrings";
import { resolveBootLanguage } from "./utils/i18n/languagePreference";
import { applyI18nLanguage } from "./utils/i18n/platformLanguage";
import { Provider } from "react-redux";
import { BrowserRouter } from "react-router-dom";
import { store } from "./store";
import { registerOutdatedAppAutoRefresh } from "./utils/appUpdateUtils";
import { applyColorVariables } from "./utils/colorUtils";
import antdTheme, { antdFormConfig } from "./theme/antdTheme";
import IpWhitelistGate from "./components/IpWhitelistGate/IpWhitelistGate";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 1,
      staleTime: 30_000,
    },
    mutations: {
      retry: 0,
    },
  },
});

// Async init before rendering
const initApp = async () => {
  try {
    const urlColors =
      process.env.REACT_APP_ADMIN_PANEL_CLOUDFRONT_URL +
      "/common_assets/colors.json";
    const responseColors = await fetch(urlColors);
    const colorPalette = await responseColors?.json();
    applyColorVariables(colorPalette);

    // String loading must not block the render: AppStrings.ts ships English
    // defaults, so a TCM outage degrades to bundled copy instead of a blank
    // screen.
    try {
      // TCM V2: strings are stored per language. Load `en` for the initial
      // render; the platform language is re-fetched once appConfig resolves
      // (see applyResolvedI18nLanguage).
      await loadAppStringsForLanguage("en");

      // Boot keying (devmode only): re-key to the stored language preference
      // (pam_guest_language > pam_admin_language > "en"). This has to run as a
      // second pass because devmode detection reads AppStrings.PAM_APP_NAME,
      // which is only authoritative after the `en` strings above have loaded.
      // Devmode off → resolveBootLanguage returns "en" and this is a no-op,
      // identical to today's boot.
      const bootLanguage = resolveBootLanguage();
      if (bootLanguage !== "en") {
        await applyI18nLanguage(bootLanguage);
      }
    } catch (error) {
      console.error(
        "Failed to load app strings; rendering with bundled defaults:",
        error
      );
    }

    // Now render the app
    const root = ReactDOM.createRoot(document.getElementById("root"));
    root.render(
      <React.StrictMode>
        <AntdLocaleProvider theme={antdTheme} form={antdFormConfig}>
          <Provider store={store}>
            <QueryClientProvider client={queryClient}>
              <BrowserRouter>
                <IpWhitelistGate>
                  <AppProvider>
                    <App />
                  </AppProvider>
                </IpWhitelistGate>
              </BrowserRouter>
            </QueryClientProvider>
          </Provider>
        </AntdLocaleProvider>
      </React.StrictMode>
    );
  } catch (error) {
    console.error("App init failed (color palette / render):", error);
    // Fallback: render with default colors
    // const root = ReactDOM.createRoot(document.getElementById("root"));
    // root.render(<App />);
  }
};

// Start app
registerOutdatedAppAutoRefresh();
initApp();
